import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Mail, Send, Inbox, Users, X, RefreshCw, Trash2, Eye, ChevronLeft, Megaphone, SendHorizontal } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import DraggableResizableWindow from "./DraggableResizableWindow";

const StaffMailPanel = ({ onClose }) => {
  const { token, user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("inbox"); // inbox, sent, compose, view
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewingFrom, setViewingFrom] = useState("inbox"); // Track which list we came from
  
  // Compose state
  const [composeData, setComposeData] = useState({
    recipient_id: "",
    subject: "",
    content: "",
    is_broadcast: false
  });
  const [sending, setSending] = useState(false);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/staff/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const fetchSentMessages = async () => {
    try {
      const response = await axios.get(`${API}/staff/messages/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSentMessages(response.data.messages || []);
    } catch (error) {
      console.error("Failed to fetch sent messages:", error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/staff/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error("Failed to fetch staff list:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchMessages(), fetchSentMessages(), fetchStaffList()]);
      setLoading(false);
    };
    loadData();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchMessages();
      fetchSentMessages();
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleSend = async () => {
    if (!composeData.subject.trim() || !composeData.content.trim()) {
      toast.error("Subject and content are required");
      return;
    }
    
    if (!composeData.is_broadcast && !composeData.recipient_id) {
      toast.error("Please select a recipient or send as broadcast");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/staff/messages/send`, composeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(composeData.is_broadcast ? "Broadcast sent to all staff!" : "Message sent!");
      setComposeData({ recipient_id: "", subject: "", content: "", is_broadcast: false });
      setActiveView("inbox");
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.post(`${API}/staff/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    
    try {
      await axios.delete(`${API}/staff/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message deleted");
      setSelectedMessage(null);
      setActiveView(viewingFrom);
      fetchMessages();
      fetchSentMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete message");
    }
  };

  const openMessage = (msg, fromView = "inbox") => {
    setSelectedMessage(msg);
    setViewingFrom(fromView);
    setActiveView("view");
    if (fromView === "inbox" && !msg.read_by?.includes(user?.user_id)) {
      handleMarkRead(msg.message_id);
    }
  };

  const isUnread = (msg) => !msg.read_by?.includes(user?.user_id);

  return (
    <DraggableResizableWindow
      title="Staff Mail System"
      onClose={onClose}
      initialWidth={600}
      initialHeight={500}
      minWidth={400}
      minHeight={350}
    >
      <div className="flex flex-col h-full bg-[#C0C0C0]" data-testid="staff-mail-panel">
        {/* Toolbar */}
        <div className="flex items-center gap-2 p-2 border-b border-[#808080]">
          <button
            onClick={() => setActiveView("inbox")}
            className={`retro-button text-xs px-3 py-1 flex items-center gap-1 ${activeView === "inbox" ? "bg-blue-500 text-white" : ""}`}
            data-testid="inbox-btn"
          >
            <Inbox className="w-3 h-3" />
            Inbox {unreadCount > 0 && <span className="bg-red-500 text-white text-[10px] px-1 rounded">{unreadCount}</span>}
          </button>
          <button
            onClick={() => setActiveView("sent")}
            className={`retro-button text-xs px-3 py-1 flex items-center gap-1 ${activeView === "sent" ? "bg-purple-500 text-white" : ""}`}
            data-testid="sent-btn"
          >
            <SendHorizontal className="w-3 h-3" />
            Sent ({sentMessages.length})
          </button>
          <button
            onClick={() => { setActiveView("compose"); setComposeData({ recipient_id: "", subject: "", content: "", is_broadcast: false }); }}
            className={`retro-button text-xs px-3 py-1 flex items-center gap-1 ${activeView === "compose" ? "bg-green-500 text-white" : ""}`}
            data-testid="compose-btn"
          >
            <Send className="w-3 h-3" />
            Compose
          </button>
          <button
            onClick={() => { fetchMessages(); fetchSentMessages(); }}
            className="retro-button text-xs px-2 py-1"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm">Loading...</p>
            </div>
          ) : activeView === "inbox" ? (
            <div className="h-full overflow-y-auto retro-scrollbar p-2">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No messages</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {messages.map(msg => (
                    <div
                      key={msg.message_id}
                      onClick={() => openMessage(msg, "inbox")}
                      className={`p-2 rounded cursor-pointer flex items-start gap-2 ${
                        isUnread(msg) ? "bg-blue-100 font-bold border-l-4 border-blue-500" : "bg-white hover:bg-gray-100"
                      }`}
                      data-testid={`message-item-${msg.message_id}`}
                    >
                      {msg.is_broadcast ? (
                        <Megaphone className="w-4 h-4 text-purple-500 flex-shrink-0 mt-1" />
                      ) : (
                        <Mail className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-600 truncate">
                            {msg.is_broadcast ? "BROADCAST" : `From: ${msg.sender_username}`}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm truncate">{msg.subject}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeView === "sent" ? (
            <div className="h-full overflow-y-auto retro-scrollbar p-2">
              {sentMessages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <SendHorizontal className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No sent messages</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {sentMessages.map(msg => (
                    <div
                      key={msg.message_id}
                      onClick={() => openMessage(msg, "sent")}
                      className="p-2 rounded cursor-pointer flex items-start gap-2 bg-white hover:bg-gray-100"
                      data-testid={`sent-message-${msg.message_id}`}
                    >
                      {msg.is_broadcast ? (
                        <Megaphone className="w-4 h-4 text-purple-500 flex-shrink-0 mt-1" />
                      ) : (
                        <SendHorizontal className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-600 truncate">
                            {msg.is_broadcast ? "BROADCAST to All Staff" : `To: ${msg.recipient_username || "Unknown"}`}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm truncate">{msg.subject}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeView === "view" && selectedMessage ? (
            <div className="h-full flex flex-col p-3">
              <button
                onClick={() => { setSelectedMessage(null); setActiveView(viewingFrom); }}
                className="retro-button text-xs px-2 py-1 w-fit mb-2 flex items-center gap-1"
              >
                <ChevronLeft className="w-3 h-3" />
                Back to {viewingFrom === "sent" ? "Sent" : "Inbox"}
              </button>
              
              <div className="retro-window flex-1 overflow-hidden flex flex-col">
                <div className="p-3 bg-[#E0E0E0] border-b border-[#808080]">
                  <div className="flex items-center justify-between">
                    <div>
                      {selectedMessage.is_broadcast && (
                        <span className="bg-purple-500 text-white text-[10px] px-2 py-0.5 rounded mr-2">BROADCAST</span>
                      )}
                      <h3 className="font-bold text-sm">{selectedMessage.subject}</h3>
                      <p className="text-xs text-gray-600">
                        {viewingFrom === "sent" ? (
                          <>To: <strong>{selectedMessage.is_broadcast ? "All Staff" : selectedMessage.recipient_username || "Unknown"}</strong></>
                        ) : (
                          <>From: <strong>{selectedMessage.sender_username}</strong></>
                        )}
                        {" • "}{new Date(selectedMessage.created_at).toLocaleString()}
                      </p>
                    </div>
                    {(selectedMessage.sender_id === user?.user_id || user?.username === 'mr_fuker') && (
                      <button
                        onClick={() => handleDelete(selectedMessage.message_id)}
                        className="retro-button text-xs px-2 py-1 bg-red-500 text-white"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex-1 p-3 overflow-y-auto retro-scrollbar bg-white">
                  <p className="text-sm whitespace-pre-wrap">{selectedMessage.content}</p>
                </div>
              </div>
            </div>
          ) : activeView === "compose" ? (
            <div className="h-full p-3 overflow-y-auto retro-scrollbar">
              <div className="retro-window p-3">
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Compose Message
                </h3>
                
                {/* Broadcast toggle (admin only) */}
                {user?.is_admin && (
                  <div className="mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={composeData.is_broadcast}
                        onChange={(e) => setComposeData(prev => ({ ...prev, is_broadcast: e.target.checked, recipient_id: "" }))}
                        className="w-4 h-4"
                      />
                      <span className="text-xs flex items-center gap-1">
                        <Megaphone className="w-3 h-3 text-purple-500" />
                        Send as Broadcast to ALL Staff
                      </span>
                    </label>
                  </div>
                )}
                
                {/* Recipient selector (if not broadcast) */}
                {!composeData.is_broadcast && (
                  <div className="mb-3">
                    <label className="text-xs font-bold block mb-1">To:</label>
                    <select
                      value={composeData.recipient_id}
                      onChange={(e) => setComposeData(prev => ({ ...prev, recipient_id: e.target.value }))}
                      className="retro-input w-full text-xs"
                      data-testid="recipient-select"
                    >
                      <option value="">Select staff member...</option>
                      {staffList.map(staff => (
                        <option key={staff.user_id} value={staff.user_id}>
                          {staff.username} {staff.is_admin ? "(Admin)" : "(Mod)"}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Subject */}
                <div className="mb-3">
                  <label className="text-xs font-bold block mb-1">Subject:</label>
                  <input
                    type="text"
                    value={composeData.subject}
                    onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Message subject..."
                    className="retro-input w-full text-xs"
                    data-testid="subject-input"
                  />
                </div>
                
                {/* Content */}
                <div className="mb-3">
                  <label className="text-xs font-bold block mb-1">Message:</label>
                  <textarea
                    value={composeData.content}
                    onChange={(e) => setComposeData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Type your message..."
                    className="retro-input w-full text-xs h-32 resize-none"
                    data-testid="content-textarea"
                  />
                </div>
                
                {/* Send button */}
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="retro-button w-full text-xs py-2 bg-green-500 text-white flex items-center justify-center gap-2"
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4" />
                  {sending ? "Sending..." : (composeData.is_broadcast ? "Send Broadcast" : "Send Message")}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </DraggableResizableWindow>
  );
};

export default StaffMailPanel;
